import { useEffect, useMemo, useState } from "react";
import { Context } from "common/types/context";
import { SchedulerFilter } from "common/types/scheduler-filters";
import { isAdminUserType } from "common/types/users";
import { SaveFilterState } from "x/records/list/types";
import { getSchedulerFiltersPermissions } from "../functions";
import { ConfigurationAction } from "./action";
import { Action } from "./types";

type ConfigurationActionsProps = {
  context: Context;
  selectedFilter: SchedulerFilter;
  isDefaultFilter: boolean;
  isSelectedFilterModified: boolean;
  onSaveViewModalShow: (state: SaveFilterState) => void;
  onFilterReset: () => void;
};

export function ConfigurationActions({
  context,
  selectedFilter,
  isDefaultFilter,
  isSelectedFilterModified,
  onFilterReset,
  onSaveViewModalShow,
}: Readonly<ConfigurationActionsProps>) {
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const { canCreate, canCreateShared, canUpdate } =
    getSchedulerFiltersPermissions(context, [
      "Create",
      "CreateShared",
      "Update",
    ]);

  const { createdBy, shared } = selectedFilter || {};
  const canEdit =
    !isDefaultFilter &&
    (isAdminUserType(context.userTypes) ||
      (canUpdate &&
        selectedFilter &&
        (!createdBy || createdBy === context.id) &&
        (!shared || canCreateShared)));

  const allActions: Action[] = useMemo(
    () => [
      {
        name: "new",
        can: isSelectedFilterModified && canCreate,
        onClick: () => onSaveViewModalShow("new"),
      },
      {
        name: "edit",
        can: !isSelectedFilterModified && canEdit,
        onClick: () => onSaveViewModalShow("edit"),
      },
      {
        name: "update",
        can: isSelectedFilterModified && !!selectedFilter && canEdit,
        onClick: () => onSaveViewModalShow("update"),
      },
      {
        name: "reset",
        can: isSelectedFilterModified && !!selectedFilter,
        onClick: onFilterReset,
      },
    ],
    [
      isSelectedFilterModified,
      canCreate,
      canEdit,
      selectedFilter,
      onSaveViewModalShow,
      onFilterReset,
    ],
  );

  const actions = selectedFilter
    ? allActions
        .filter((action) => action.can)
        .map(({ name, onClick }) => (
          <ConfigurationAction key={name} actionName={name} onClick={onClick} />
        ))
    : [];

  const hasActions = actions.length > 0;

  useEffect(() => {
    setTimeout(() => {
      setIsDisabled(!hasActions);
    });
  }, [hasActions]);

  return (
    <div className="dropdown">
      <button
        className="x-filter-action-button x-button-cancel"
        disabled={isDisabled}
        data-bs-toggle="dropdown"
        title={_("Actions")}
        data-original-title={_("Actions")}
        data-placement="bottom"
      >
        <i className="fa fa-bars" />
      </button>
      <ul className="dropdown-menu">{actions}</ul>
    </div>
  );
}
